import { useEffect, useState, type FormEvent } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowRight, CircleNotch, X } from "@phosphor-icons/react";
import { api, ApiError, type WallAsk, type WallAskRejection } from "../../api";
import { noticeWallCopy, seaCopy } from "../../content";
import { StampMark } from "./Waypoint";

/* The full-screen Notice Wall: a real harbour board with live community asks
 * pinned as index cards. Cards come from the backend; new cards pass the
 * harbourmaster (AI moderation) before they are pinned. */

const MIN_ASK_LENGTH = 8;

type BoardState = { status: "loading" | "ready" | "offline"; asks: WallAsk[] };

type SubmitState =
  | { state: "idle" }
  | { state: "sending" }
  | { state: "pinned" }
  | { state: "rejected"; reason: string }
  | { state: "error"; message: string };

/* Deterministic tilt per card so the wall looks hand-pinned but never
 * reshuffles between renders or visitors. */
const ROTATIONS = [-2.4, -1.5, -0.7, 0.8, 1.6, 2.3];
function rotationFor(key: string) {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return ROTATIONS[h % ROTATIONS.length];
}

/* The drawn pushpin from the OneAsk board idiom: ink needle, signal head. */
function BoardPin({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 26" className={className} aria-hidden focusable="false">
      <line x1="10" y1="12" x2="10" y2="24" className="stroke-ink" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="10" cy="7.5" r="5.5" className="fill-signal stroke-ink" strokeWidth="1.8" />
    </svg>
  );
}

function isRejection(body: unknown): body is WallAskRejection {
  return (
    typeof body === "object" &&
    body !== null &&
    (body as WallAskRejection).rejected === true &&
    typeof (body as WallAskRejection).reason === "string"
  );
}

function AskCard({
  text,
  signedLine,
  rotate,
  isNew,
  delay,
  prefersReduced,
}: {
  text: string;
  signedLine: string | null;
  rotate: number;
  isNew: boolean;
  delay: number;
  prefersReduced: boolean;
}) {
  return (
    <motion.li
      layout={!prefersReduced}
      initial={prefersReduced ? false : isNew ? { opacity: 0, scale: 1.14, rotate: rotate - 7, y: -14 } : { opacity: 0, y: 10, rotate }}
      animate={{ opacity: 1, scale: 1, rotate, y: 0 }}
      transition={{ duration: isNew ? 0.45 : 0.4, delay, ease: [0.16, 1, 0.3, 1] }}
      className="relative rounded-md border-2 border-ink bg-foam p-5 pt-6 shadow-card"
    >
      <BoardPin className="absolute -top-3 left-1/2 w-4 -translate-x-1/2" />
      <p className="font-mono text-[10px] tracking-widest text-muted uppercase">{oneAskLabel}</p>
      <p className="mt-1.5 leading-snug font-semibold text-ink">{text}</p>
      {signedLine && <p className="mt-3 font-mono text-[10px] text-muted">{signedLine}</p>}
    </motion.li>
  );
}

const oneAskLabel = seaCopy.landmarks.noticewall.label;

export function NoticeWallBoard({ onClose }: { onClose: () => void }) {
  const prefersReduced = useReducedMotion() ?? false;
  const c = noticeWallCopy;
  const wall = seaCopy.landmarks.noticewall;

  const [board, setBoard] = useState<BoardState>({ status: "loading", asks: [] });
  const [text, setText] = useState("");
  const [signedBy, setSignedBy] = useState("");
  const [submit, setSubmit] = useState<SubmitState>({ state: "idle" });
  const [justPinnedId, setJustPinnedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.listWallAsks().then(
      (rows) => {
        if (!cancelled) setBoard({ status: "ready", asks: rows });
      },
      () => {
        if (!cancelled) setBoard({ status: "offline", asks: [] });
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const trimmed = text.trim();
  const canSubmit = trimmed.length >= MIN_ASK_LENGTH && submit.state !== "sending";

  const onSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!canSubmit) return;
    setSubmit({ state: "sending" });
    try {
      const pinned = await api.createWallAsk({ text: trimmed, signedBy: signedBy.trim() });
      setBoard((prev) => ({ ...prev, status: "ready", asks: [pinned, ...prev.asks] }));
      setJustPinnedId(pinned.id);
      setText("");
      setSignedBy("");
      setSubmit({ state: "pinned" });
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && isRejection(err.body)) {
        setSubmit({ state: "rejected", reason: err.body.reason });
      } else {
        setSubmit({ state: "error", message: c.moderation.unavailable });
      }
    }
  };

  return (
    <motion.div
      initial={prefersReduced ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22 }}
      onClick={onClose}
      className="absolute inset-0 z-20 flex bg-ink/25 p-2.5 sm:p-5 md:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={wall.name}
    >
      <motion.div
        initial={prefersReduced ? false : { opacity: 0, y: 22, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.99 }}
        transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="notice-board relative mx-auto flex min-h-0 w-full max-w-6xl flex-1 flex-col overflow-hidden rounded-md border-2 border-ink shadow-lift"
      >
        <header className="flex items-start justify-between gap-4 border-b-2 border-ink bg-foam/90 px-5 py-4 backdrop-blur-sm md:px-8">
          <div className="min-w-0">
            <p className="font-mono text-[10px] tracking-[0.2em] text-signal-deep uppercase">{c.label}</p>
            <h2 className="mt-1 display-type text-[clamp(1.35rem,3.2vw,2.1rem)] text-ink">{wall.title}</h2>
            <p className="mt-1 hidden max-w-2xl text-sm text-ink-soft sm:block">{wall.sub}</p>
          </div>
          <button
            onClick={onClose}
            aria-label={c.close}
            className="flex size-11 shrink-0 items-center justify-center rounded-full border-2 border-ink bg-foam text-ink transition-colors hover:bg-paper-deep"
          >
            <X weight="bold" className="size-4" aria-hidden />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-5 md:px-8">
          <p aria-live="polite" className="mb-4 font-mono text-[11px] text-ink-soft">
            {board.status === "loading" && c.loading}
            {board.status === "offline" && c.offline}
            {board.status === "ready" && c.boardNote}
          </p>

          <ul className="grid grid-cols-1 items-start gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {/* A blank card waiting for the visitor's ask. */}
            <motion.li
              layout={!prefersReduced}
              initial={prefersReduced ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0, rotate: -0.6 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-md border-2 border-dashed border-ink bg-paper p-5 pt-6 shadow-card"
            >
              <BoardPin className="absolute -top-3 left-1/2 w-4 -translate-x-1/2" />
              <form onSubmit={onSubmit} noValidate>
                <h3 className="display-type text-lg text-ink">{c.form.title}</h3>
                <label htmlFor="wall-ask-text" className="sr-only">
                  {c.form.title}
                </label>
                <textarea
                  id="wall-ask-text"
                  value={text}
                  maxLength={c.form.maxLength}
                  onChange={(e) => {
                    setText(e.target.value);
                    if (submit.state !== "sending") setSubmit({ state: "idle" });
                  }}
                  placeholder={c.form.placeholder}
                  rows={3}
                  className="mt-3 w-full resize-none rounded-md border-2 border-ink bg-foam p-3 text-sm font-medium text-ink placeholder:text-muted"
                />
                <div className="mt-1 flex items-center justify-between gap-3">
                  <label htmlFor="wall-ask-signed" className="font-mono text-[10px] tracking-widest text-muted uppercase">
                    {c.form.signedLabel}
                  </label>
                  <span className="font-mono text-[10px] text-muted">
                    {text.length}/{c.form.maxLength}
                  </span>
                </div>
                <input
                  id="wall-ask-signed"
                  value={signedBy}
                  maxLength={40}
                  onChange={(e) => setSignedBy(e.target.value)}
                  placeholder={c.form.signedPlaceholder}
                  className="mt-1 h-10 w-full rounded-md border-2 border-ink bg-foam px-3 font-hand text-lg text-ink placeholder:font-sans placeholder:text-sm placeholder:text-muted"
                />
                <p className="mt-2 font-mono text-[10px] leading-relaxed text-muted">{c.form.note}</p>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="mt-3 inline-flex h-11 items-center gap-2 rounded-full bg-ink px-5 text-sm font-semibold text-foam transition-transform duration-200 ease-out hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-40 disabled:hover:translate-y-0 motion-reduce:hover:translate-y-0"
                >
                  {submit.state === "sending" ? (
                    <CircleNotch weight="bold" className="size-4 motion-safe:animate-spin" aria-hidden />
                  ) : (
                    <ArrowRight weight="bold" className="size-4" aria-hidden />
                  )}
                  {c.form.submit}
                </button>

                <div aria-live="polite">
                  {submit.state === "sending" && (
                    <p className="mt-2 font-mono text-[11px] text-muted">{c.form.sending}</p>
                  )}
                  {submit.state === "pinned" && (
                    <p className="mt-2 flex items-center gap-2 font-mono text-[11px] text-ink">
                      <StampMark className="w-7 shrink-0" />
                      {c.moderation.pinned}
                    </p>
                  )}
                  {submit.state === "error" && (
                    <p className="mt-2 font-mono text-[11px] text-signal-deep">{submit.message}</p>
                  )}
                  <AnimatePresence>
                    {submit.state === "rejected" && (
                      <motion.div
                        initial={prefersReduced ? false : { opacity: 0, y: 8, rotate: -2 }}
                        animate={{ opacity: 1, y: 0, rotate: -0.8 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-3 rounded-md border-2 border-ink bg-manila p-3"
                      >
                        <p className="font-mono text-[10px] tracking-widest text-muted uppercase">
                          {c.moderation.rejectedLabel}
                        </p>
                        <p className="mt-1 font-hand text-xl leading-tight text-ink">{submit.reason}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </form>
            </motion.li>

            {board.asks.map((ask, i) => (
              <AskCard
                key={ask.id}
                text={ask.text}
                signedLine={ask.signedBy}
                rotate={rotationFor(ask.id)}
                isNew={ask.id === justPinnedId}
                delay={ask.id === justPinnedId ? 0 : Math.min(i * 0.035, 0.35)}
                prefersReduced={prefersReduced}
              />
            ))}

            {/* The founding crew's original cards close out the wall. */}
            {wall.asks.map((founding, i) => (
              <AskCard
                key={founding}
                text={founding}
                signedLine={c.founding}
                rotate={ROTATIONS[(i * 2 + 1) % ROTATIONS.length]}
                isNew={false}
                delay={Math.min((board.asks.length + i) * 0.035, 0.4)}
                prefersReduced={prefersReduced}
              />
            ))}
          </ul>
        </div>
      </motion.div>
    </motion.div>
  );
}
